import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Flame, Droplets, TrendingDown, PieChart } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BurnPage() {
    const [data, setData] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await fetch(`${API}/api/burn/stats`);
                setData(await res.json());
            } catch (e) { console.error(e); }
        };
        load();
        const i = setInterval(load, 20000);
        return () => clearInterval(i);
    }, []);

    const burn = data?.burn || {};
    const treasury = data?.treasury || {};

    return (
        <div className="min-h-screen py-10 px-4 sm:px-6 lg:px-8" data-testid="burn-page">
            <div className="max-w-6xl mx-auto">
                <div className="flex items-center gap-3 mb-8">
                    <Flame className="w-10 h-10 text-[#FF6B00]" />
                    <div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white">Auto Buy-Back & Burn</h1>
                        <p className="text-white/60 mt-1">À chaque pari résolu, la plateforme brûle automatiquement du B4P et alimente une réserve de liquidité. <span className="text-[#FF6B00] font-bold">Déflation programmée.</span></p>
                    </div>
                </div>

                {/* Burn Stats Hero */}
                <div className="grid sm:grid-cols-2 gap-6 mb-8">
                    <Card className="bg-gradient-to-br from-[#FF6B00]/20 to-black border-[#FF6B00]/40 overflow-hidden relative">
                        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 70% 30%, #FF6B00, transparent 60%)' }} />
                        <CardContent className="p-8 relative">
                            <div className="flex items-center gap-2 text-white/60 text-sm uppercase tracking-widest mb-2"><Flame className="w-4 h-4 text-[#FF6B00]" />B4P Brûlés à vie</div>
                            <div className="text-5xl font-black text-[#FF6B00]" data-testid="total-burned">{burn.total_burned_b4p?.toLocaleString()} B4P</div>
                            <div className="text-sm text-white/40 mt-2">{burn.burn_count} opérations · déflation programmée</div>
                        </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-[#00FF66]/20 to-black border-[#00FF66]/40 overflow-hidden relative">
                        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 70% 30%, #00FF66, transparent 60%)' }} />
                        <CardContent className="p-8 relative">
                            <div className="flex items-center gap-2 text-white/60 text-sm uppercase tracking-widest mb-2"><Droplets className="w-4 h-4 text-[#00FF66]" />Réserve de liquidité</div>
                            <div className="text-5xl font-black text-[#00FF66]" data-testid="total-liquidity">{burn.total_liquidity_reserve_eth?.toFixed(6)} ETH</div>
                            <div className="text-sm text-white/40 mt-2">{burn.liquidity_count} allocations · prêtes pour Aerodrome</div>
                        </CardContent>
                    </Card>
                </div>

                {/* Breakdown */}
                <Card className="bg-white/5 border-white/10 mb-8">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2"><PieChart className="w-5 h-5" />Répartition des frais plateforme (3%)</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div className="p-4 bg-black/30 rounded-lg border border-white/5">
                                <div className="text-xs text-white/50">Master Wallet</div>
                                <div className="text-lg font-bold text-white">50% ETH</div>
                                <div className="text-xs text-white/40">retenu</div>
                            </div>
                            <div className="p-4 bg-[#FFD700]/10 rounded-lg border border-[#FFD700]/20">
                                <div className="text-xs text-white/50">Buy-Back B4P</div>
                                <div className="text-lg font-bold text-[#FFD700]">50% ETH</div>
                                <div className="text-xs text-white/40">→ B4P acheté</div>
                            </div>
                            <div className="p-4 bg-[#FF6B00]/10 rounded-lg border border-[#FF6B00]/20">
                                <div className="text-xs text-white/50 flex items-center gap-1.5"><Flame className="w-3 h-3" /> Burn</div>
                                <div className="text-lg font-bold text-[#FF6B00]">15% fee</div>
                                <div className="text-xs text-white/40">détruit</div>
                            </div>
                            <div className="p-4 bg-[#00FF66]/10 rounded-lg border border-[#00FF66]/20">
                                <div className="text-xs text-white/50">Liquidité DEX</div>
                                <div className="text-lg font-bold text-[#00FF66]">15% fee</div>
                                <div className="text-xs text-white/40">Aerodrome</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Treasury Stats */}
                <Card className="bg-white/5 border-white/10 mb-8">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2"><TrendingDown className="w-5 h-5" />Treasury — Conversion ETH → B4P</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                            <div><div className="text-xs text-white/50">ETH collecté</div><div className="text-xl font-bold text-white">{treasury.total_eth_collected?.toFixed(6)} ETH</div></div>
                            <div><div className="text-xs text-white/50">ETH swappé</div><div className="text-xl font-bold text-[#9B6FE8]">{treasury.total_eth_swapped?.toFixed(6)} ETH</div></div>
                            <div><div className="text-xs text-white/50">B4P acheté</div><div className="text-xl font-bold text-[#FFD700]">{treasury.total_b4p_bought?.toLocaleString()} B4P</div></div>
                            <div><div className="text-xs text-white/50">B4P distribué (MLM)</div><div className="text-xl font-bold text-[#00FF66]">{treasury.total_b4p_distributed?.toLocaleString()} B4P</div></div>
                            <div><div className="text-xs text-white/50">ETH retenu</div><div className="text-xl font-bold text-white">{treasury.total_eth_retained?.toFixed(6)} ETH</div></div>
                            <div><div className="text-xs text-white/50">Taux actuel</div><div className="text-xl font-bold text-white">1 ETH = {treasury.current_rate?.toLocaleString()} B4P</div></div>
                        </div>
                    </CardContent>
                </Card>

                {/* Recent Burns */}
                <Card className="bg-white/5 border-white/10">
                    <CardHeader><CardTitle className="text-white flex items-center gap-2"><Flame className="w-4 h-4 text-[#FF6B00]" />Burns récents</CardTitle></CardHeader>
                    <CardContent>
                        {(burn.recent_burns || []).length === 0 ? (
                            <div className="text-center text-white/40 py-6">Aucun burn pour le moment. Chaque pari résolu en déclenchera un.</div>
                        ) : (
                            <div className="space-y-2">
                                {burn.recent_burns.map((b, i) => (
                                    <div key={i} className="flex items-center justify-between p-3 bg-black/20 rounded-lg border border-white/5">
                                        <div>
                                            <Badge className="bg-[#FF6B00]/10 text-[#FF6B00] border-[#FF6B00]/30">{b.source_type}</Badge>
                                            <span className="text-white/60 text-xs ml-2 font-mono">{b.source_id?.slice(0, 12)}...</span>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[#FF6B00] font-bold">-{b.b4p_burned} B4P</div>
                                            <div className="text-xs text-white/40">{b.eth_spent} ETH spent</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
